<template>
  <div class="about">
    <page-header :title="model.data && model.data.name" :desc="model.data && model.data.name">
      <li class="breadcrumb-item"><router-link :to="{name:'applicationWindow.index'}">{{ $tc('applicationWindow.name', 2) }}</router-link></li>
      <li class="breadcrumb-item">{{ model.data && model.data.name }}</li>
    </page-header>
    <page-content>

      <!-- Edit stepper -->
      <template v-if="editMode">
        <div class="wizard-steps">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="wizard-step"
            :class="{ 'active': currentStep === index, 'completed': currentStep > index }"
            @click="goToStep(index)"
          >
            <div class="wizard-step-circle">
              <i v-if="currentStep > index" class="fa fa-check"></i>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <div class="wizard-step-label">{{ step.label }}</div>
          </div>
          <div class="wizard-line"></div>
        </div>

        <ntm-block :collapsable="false">
          <template v-if="steps[currentStep] && steps[currentStep].type === 'basic'">
            <formly-form :form="editForm" :model="editModel" :fields="basicFieldsBeforeCategories"></formly-form>

            <label class="mb-0">{{ $tc('category.name', 2) }}</label>
            <categories-manager v-model="editModel.categories" class="mb-3" />
            <span v-if="categoriesErrorMessage" class="help-block form-text text-danger">{{ categoriesErrorMessage }}</span>

            <formly-form :form="editForm" :model="editModel" :fields="basicFieldsUntilPlayerTags"></formly-form>

            <player-tags-select v-if="editModel.collectPlayerTags" v-model="editModel.playerTagUuids" :errors="editForm.$errors" />

            <formly-form :form="editForm" :model="editModel" :fields="basicFieldsAfterPlayerTags"></formly-form>
          </template>
          <formly-form v-else :form="editForm" :model="editModel" :fields="currentFields"></formly-form>

          <template v-if="steps[currentStep] && steps[currentStep].type === 'team'">
            <hr>
            <h5 class="mb-3">{{ $t('customField.title') }}</h5>
            <custom-fields-manager v-model="editModel.customFields" application-type="TEAM" />
          </template>

          <template v-if="steps[currentStep] && steps[currentStep].type === 'freeAgent'">
            <hr>
            <h5 class="mb-3">{{ $t('customField.title') }}</h5>
            <custom-fields-manager v-model="editModel.customFields" application-type="FREE_AGENT" />
          </template>

          <div class="d-flex justify-content-between mt-4 mb-3">
            <div class="d-flex" style="gap: 8px;">
              <button
                v-if="currentStep > 0"
                type="button"
                class="btn btn-light"
                @click="prevStep"
              >
                <i class="fa fa-arrow-left mr-1"></i>{{ $t('action.back') }}
              </button>
              <button type="button" class="btn btn-light" @click="cancelEdit">
                {{ $t('action.cancel') }}
              </button>
            </div>

            <button
              v-if="currentStep < steps.length - 1"
              type="button"
              class="btn btn-primary"
              @click="nextStep"
            >
              {{ $t('action.next') }}<i class="fa fa-arrow-right ml-1"></i>
            </button>
            <button
              v-else
              type="button"
              class="btn btn-success"
              :disabled="saving"
              @click="onSubmit"
            >
              <span>{{ $t('save') }}</span>
              <i v-if="!saving" class="fa fa-save ml-1"></i>
              <i v-else class="fa fa-cog fa-spin ml-1"></i>
            </button>
          </div>
        </ntm-block>
      </template>

      <!-- Normal show content -->
      <template v-else>
        <ntm-block :title="$tc('action.name', 2)">
          <template slot="options">
            <span v-if="model.data && model.data.isArchived" class="badge badge-secondary mr-2">{{ $t('applicationWindow.archived') }}</span>
            <button v-if="model.data && !model.data.isArchived" @click="archiveApplicationWindow()" type="button" class="btn btn-sm btn-warning mr-2">{{ $t('applicationWindow.archive') }}<i class="fa fa-archive ml-2"></i></button>
            <button @click="startEdit()" type="button" class="btn btn-sm btn-info">{{ $t('action.edit') }}<i class="fa fa-pencil ml-2"></i></button>
          </template>
        </ntm-block>

        <ntm-block :title="$tc('category.name', 2)" :save-collapse="`application-window-${model.uuid}-categories`" :init-collapse="true">
          <div class="row">
            <div v-for="(category, n) in model.categoriesWithCount" :key="n" class="col-sm-6 col-md-3">
              <a class="block block-bordered block-rounded block-link-shadow" href="javascript:void(0)" :class="{'border-success': selectedCategoryFilter.includes(n)}" @click="toogleCategory(n)">
                <div class="block-content block-content-full d-flex align-items-center justify-content-between">
                  <span class="fw-semibold text-uppercase fs-sm">{{ n }}</span>
                  <span class="badge bg-white">{{ category.total }}</span>
                </div>
              </a>
            </div>
          </div>
        </ntm-block>

        <ntm-block>
          <team-applications :application-window-uuid="$route.params.applicationWindow" :categories="selectedCategoryFilter"></team-applications>
        </ntm-block>
      </template>

    </page-content>
  </div>
</template>

<script>
import ApplicationWindowService from '@/services/applicationWindow.service'
import { useRouteQuery } from '@/use/useRouteQuery'
import TeamApplications from '@/views/applicationWindows/components/TeamApplications.vue'
import CustomFieldsManager from '@/views/applicationWindows/components/CustomFieldsManager.vue'
import CategoriesManager from '@/views/applicationWindows/components/CategoriesManager.vue'
import PlayerTagsSelect from '@/views/applicationWindows/components/PlayerTagsSelect.vue'
import { useTeamApplications } from '@/use/useTeamApplications'
import fieldsStep1 from './formDefinitionStep1'
import fieldsStep2 from './formDefinitionStep2'
import fieldsStep3 from './formDefinitionStep3'

export default {
  components: { TeamApplications, CustomFieldsManager, CategoriesManager, PlayerTagsSelect },
  setup () {
    const selectedCategories = useRouteQuery('category', null, 'multi')
    const statuses = useRouteQuery('statuses')
    const { fetchPendingTeamApplicationsCount } = useTeamApplications()
    return { selectedCategories, statuses, fetchPendingTeamApplicationsCount }
  },
  data () {
    return {
      model: { data: null, categoriesWithCount: {} },
      editMode: false,
      currentStep: 0,
      editModel: {},
      editForm: {},
      saving: false
    }
  },
  computed: {
    steps () {
      const result = [
        { label: this.$t('applicationWindow.stepBasic'), fields: fieldsStep1, type: 'basic' }
      ]
      if (this.editModel.teamApplicationEnabled) {
        result.push({ label: this.$t('applicationWindow.stepTeam'), fields: fieldsStep2, type: 'team' })
      }
      if (this.editModel.freeAgentApplicationEnabled) {
        result.push({ label: this.$t('applicationWindow.stepFreeAgent'), fields: fieldsStep3, type: 'freeAgent' })
      }
      return result
    },
    currentFields () {
      return this.steps[this.currentStep].fields
    },
    basicFieldsBeforeCategories () {
      return fieldsStep1.filter(f => f.key === 'name')
    },
    basicFieldsUntilPlayerTags () {
      const fields = fieldsStep1.filter(f => f.key !== 'name')
      return fields.slice(0, fields.findIndex(f => f.key === 'collectPlayerTags') + 1)
    },
    basicFieldsAfterPlayerTags () {
      const fields = fieldsStep1.filter(f => f.key !== 'name')
      return fields.slice(fields.findIndex(f => f.key === 'collectPlayerTags') + 1)
    },
    categoriesErrorMessage () {
      const errors = this.editForm.$errors && this.editForm.$errors.categories
      if (!errors) return null
      const errorKey = Object.keys(errors)[0]
      if (errorKey === undefined || typeof errors[errorKey] === 'boolean') return null
      const raw = errors[errorKey]
      if (raw.includes(' ')) return raw
      const parts = raw.split('.')
      const errorTypeParts = parts[parts.length - 1].split('|')
      const errorTypeName = errorTypeParts.shift()
      return this.$t('validation.categories.' + errorTypeName, errorTypeParts)
    },
    selectedCategoryFilter: {
      get () {
        return this.selectedCategories || []
      },
      set (value) {
        this.selectedCategories = value
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    ApplicationWindowService.show(to.params.applicationWindow).then((response) => {
      next(vm => vm.setData(response.data))
    })
  },
  methods: {
    fetchData () {
      ApplicationWindowService.show(this.$route.params.applicationWindow).then((response) => {
        this.setData(response.data)
      })
    },
    setData (data) {
      this.model = data
    },
    startEdit () {
      this.currentStep = 0
      this.editForm = {}
      this.editModel = {
        ...this.model.data,
        customFields: [...(this.model.data.customFields || [])],
        categories: [...(this.model.data.categories || [])],
        playerTagUuids: (this.model.data.playerTags || []).map(tag => tag.uuid),
        availablePlayerPositions: this.model.data.availablePlayerPositions || []
      }
      this.$nextTick(() => {
        this.editMode = true
      })
    },
    cancelEdit () {
      this.editMode = false
      this.currentStep = 0
    },
    goToStep (index) {
      if (index < this.currentStep) {
        this.currentStep = index
      }
    },
    nextStep () {
      const step = this.steps[this.currentStep]
      let validate = null
      if (step.type === 'basic') validate = () => ApplicationWindowService.validateStep1(this.editModel)
      if (step.type === 'team') validate = () => ApplicationWindowService.validateStep2(this.editModel)

      if (!validate) {
        this.currentStep++
        return
      }

      validate().then(() => {
        this.editForm.$errors = {}
        this.currentStep++
      }).catch((err) => {
        this.editForm.$errors = err.response.data.errors
      })
    },
    prevStep () {
      if (this.currentStep > 0) {
        this.currentStep--
      }
    },
    goToFirstStepWithError (errors) {
      const errorKeys = Object.keys(errors)
      const stepIndex = this.steps.findIndex(step =>
        step.fields.some(f => errorKeys.includes(f.key)) ||
        (step.type === 'basic' && errorKeys.includes('categories'))
      )
      if (stepIndex !== -1) {
        this.currentStep = stepIndex
      }
    },
    onSubmit () {
      this.saving = true
      ApplicationWindowService.update(this.model.data.uuid, this.editModel).then(() => {
        this.fetchData()
        this.editMode = false
        this.currentStep = 0
      }).catch((err) => {
        const errors = err.response.data.errors
        this.editForm.$errors = errors
        this.goToFirstStepWithError(errors)
      }).finally(() => {
        this.saving = false
      })
    },
    toogleCategory (category) {
      const categories = [...this.selectedCategoryFilter]
      const index = categories.findIndex(c => c === category)
      if (index > -1) {
        categories.splice(index, 1)
      } else {
        categories.push(category)
      }
      this.selectedCategories = categories.length === 0 ? null : categories
    },
    archiveApplicationWindow () {
      if (!window.confirm(this.$t('applicationWindow.archiveConfirm'))) { return }
      ApplicationWindowService.archive(this.model.data.uuid).then(() => {
        this.fetchData()
        this.fetchPendingTeamApplicationsCount()
      })
    }
  }
}
</script>

<style scoped>
.wizard-steps {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 0 16px;
}
.wizard-line {
  position: absolute;
  top: 20px;
  left: calc(16px + 40px);
  right: calc(16px + 40px);
  height: 2px;
  background: #e4e7ed;
  z-index: 0;
}
.wizard-step {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default;
  flex: 1;
}
.wizard-step.completed {
  cursor: pointer;
}
.wizard-step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e4e7ed;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
  transition: background 0.2s, color 0.2s;
  margin-bottom: 8px;
}
.wizard-step.active .wizard-step-circle,
.wizard-step.completed .wizard-step-circle {
  background: #008078;
  color: #fff;
}
.wizard-step-label {
  font-size: 13px;
  font-weight: 600;
  color: #999;
  text-align: center;
}
.wizard-step.active .wizard-step-label,
.wizard-step.completed .wizard-step-label {
  color: #008078;
}
</style>
